import { Connector, HarnessNode, Wire } from '../types';
import { Bounds, connectorBounds, nodeBounds } from './selection';
import { connectorHeight } from './connectorLayout';

/** A viewport transform: world point (x,y) maps to screen as world * scale + offset. */
export interface FitTransform {
  x: number;
  y: number;
  scale: number;
}

export interface FitOptions {
  /** Screen-pixel margin left on every side of the content. */
  padding: number;
  minScale: number;
  maxScale: number;
}

/** Union bounding box of all connectors and nodes, or null when there is nothing to fit. */
export function contentBounds(connectors: Connector[], nodes: HarnessNode[], wires: Wire[] = []): Bounds | null {
  const boxes: Bounds[] = [
    ...connectors.map(connector => connectorBounds(connector, connectorHeight(connector, wires))),
    ...nodes.map(nodeBounds)
  ];
  if (boxes.length === 0) {
    return null;
  }
  return boxes.reduce((acc, box) => ({
    minX: Math.min(acc.minX, box.minX),
    minY: Math.min(acc.minY, box.minY),
    maxX: Math.max(acc.maxX, box.maxX),
    maxY: Math.max(acc.maxY, box.maxY)
  }));
}

/**
 * Computes the viewport transform that centers `bounds` inside a viewport of the
 * given pixel size, scaled to fit with padding. Scale is clamped to
 * [minScale, maxScale] so a tiny graph is not blown up nor a huge one shrunk away.
 */
export function computeFitTransform(
  bounds: Bounds,
  viewWidth: number,
  viewHeight: number,
  options: FitOptions
): FitTransform {
  const contentWidth = Math.max(1, bounds.maxX - bounds.minX);
  const contentHeight = Math.max(1, bounds.maxY - bounds.minY);
  const availableWidth = Math.max(1, viewWidth - options.padding * 2);
  const availableHeight = Math.max(1, viewHeight - options.padding * 2);

  const rawScale = Math.min(availableWidth / contentWidth, availableHeight / contentHeight);
  const scale = Math.max(options.minScale, Math.min(options.maxScale, rawScale));

  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  return {
    scale,
    x: viewWidth / 2 - centerX * scale,
    y: viewHeight / 2 - centerY * scale
  };
}
